<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '520px'"
		:persistent="confirmInviteButtonLoading"
		@click:outside="handleDismiss"
		@keydown.esc="handleDismiss"
	>
		<v-card
			class="sgnl-pb-0"
			data-test="invite-member-dialog-sheet"
		>
			<v-card-title class="dialog-header sgnl-subtitle-medium"
				>Invite Member
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="handleDismiss"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<v-card-text class="dialog-body sgnl-paragraph-s-regular">
				<div>Invite a new member to join your organization.</div>

				<v-form
					ref="inviteForm"
					class="stack"
				>
					<div class="input-group">
						<div class="field-label">Email Address</div>
						<v-text-field
							v-model="email"
							placeholder="member@example.com"
							:rules="emailRules"
							data-test="invite-member-email-input"
							hide-details="auto"
						/>
					</div>

					<div class="input-group">
						<div class="field-label">Role</div>
						<v-select
							v-model="role"
							:items="roleOptions"
							:rules="roleRules"
							hide-details
							data-test="invite-member-role-selector"
						/>
					</div>
				</v-form>

				<v-alert
					v-if="errorText !== ''"
					density="compact"
					transition="v-expand-transition"
					class="sgnl-paragraph-s-medium"
					border="start"
					type="error"
				>
					<span>{{ errorText }}</span>
				</v-alert>
			</v-card-text>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					@click="handleDismiss"
				>
					Cancel
				</v-btn>
				<v-btn
					variant="flat"
					color="primary"
					@click="confirmInvite"
					data-test="invite-member-confirm-button"
					:loading="confirmInviteButtonLoading"
					:disabled="!isFormValid"
				>
					Send Invite
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";

// Types
import { OrganizationRole } from "@/types/api/organizations";

// Libraries
import { sdk } from "@/js/sdk";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";

// Utilities
import * as Sentry from "@sentry/vue";

// Stores
import { useUserStore } from "@/stores/user";

type ComponentData = {
	email: string;
	role: OrganizationRole | null;
	roleOptions: OrganizationRole[];
	confirmInviteButtonLoading: boolean;
	errorText: string;
	emailRules: ((v: string) => boolean | string)[];
	roleRules: ((v: OrganizationRole | null) => boolean | string)[];
	organizationId: string | null;
};

export default defineComponent({
	name: "InviteMember",
	components: { Close16 },
	emits: ["update:showDialog", "memberInvited"],
	data: (): ComponentData => ({
		email: "",
		role: "Member",
		roleOptions: ["Owner", "Member"],
		confirmInviteButtonLoading: false,
		errorText: "",
		organizationId: null,
		emailRules: [
			(v: string) => !!v || "Email is required",
			(v: string) => {
				if (!v) return true;
				if (/\s/.test(v)) return "Email address cannot have spaces.";
				if (!/@/.test(v)) return "Email is missing the @ symbol.";
				if (v.split("@").length > 2) return "Email cannot have multiple @ symbols.";
				const parts = v.split("@");
				if (parts[0].length === 0) return "Email must have content before @.";
				if (parts[1].length === 0) return "Email must have content after @.";
				if (!/\./.test(parts[1])) return "Email domain must contain a period (e.g., .com, .org).";
				if (parts[1].startsWith(".") || parts[1].endsWith("."))
					return "Email domain cannot start or end with a period.";
				return true;
			},
			(v: string) => v.length <= 254 || "Email is too long (maximum 254 characters).",
		],
		roleRules: [(v: OrganizationRole | null) => !!v || "Role is required"],
	}),
	props: {
		showDialog: { type: Boolean, default: false },
	},
	computed: {
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
		isFormValid(): boolean {
			if (!this.email || this.role === null) return false;
			const parts = this.email.split("@");
			return (
				parts.length === 2 &&
				parts[0].length > 0 &&
				parts[1].length > 0 &&
				parts[1].includes(".") &&
				!parts[1].startsWith(".") &&
				!parts[1].endsWith(".") &&
				this.email.length <= 254
			);
		},
	},
	setup() {
		const userStore = useUserStore();
		return { userStore };
	},
	methods: {
		handleDismiss() {
			if (this.confirmInviteButtonLoading) {
				return;
			}
			this.closeDialog();
		},
		closeDialog() {
			this.email = "";
			this.role = null;
			this.errorText = "";
			this.$emit("update:showDialog", false);
		},
		async confirmInvite() {
			this.confirmInviteButtonLoading = true;
			this.errorText = "";

			if (!this.organizationId || !this.role) {
				this.errorText = "Organization ID or role is missing.";
				this.confirmInviteButtonLoading = false;
				return;
			}

			try {
				const response = await sdk.organizations.inviteUser(this.organizationId, {
					Email: this.email,
					Role: this.role,
				});

				// Emit success event with message
				const message =
					response.Status === "Pending"
						? `Invitation sent to ${this.email}. They will be added to the organization upon signup.`
						: `${this.email} has been added to the organization.`;

				this.$emit("memberInvited", {
					email: this.email,
					role: this.role,
					message: message,
				});
				this.$posthog?.capture("team_member_invited", { role: this.role });
				this.closeDialog();
			} catch (error) {
				let severityLevel: Sentry.SeverityLevel = "error";
				if (error instanceof SdkError) {
					switch (error.code) {
						case ERROR_CODES.API_SERVER_ERROR:
							severityLevel = "fatal";
							this.errorText =
								`Our system encountered a server error sending the invitation.` +
								" Please try again. If this error persists, please contact support at developer-support@signaloid.com.";
							break;
						case ERROR_CODES.API_UNAUTHORIZED:
							this.errorText =
								"Our system encountered an authorization error while sending the invitation." +
								" Please try refreshing the page. Signing out and back in will ensure you are properly authorized." +
								" If this error persists, please contact support at developer-support@signaloid.com.";
							break;
						case ERROR_CODES.API_BAD_REQUEST:
							this.errorText =
								"The invitation could not be sent. Please check that the email address is valid." +
								" If this error persists, please contact support at developer-support@signaloid.com.";
							break;

						default:
							break;
					}
				} else {
					this.errorText =
						"A request to send the invitation was initiated but there was no response." +
						" Please ensure that you have an active Internet connection." +
						" If this error persists, please contact support at developer-support@signaloid.com.";
				}
				console.log(error);
				Sentry.captureException(error, {
					level: severityLevel,
					extra: { userMessage: this.errorText },
					tags: { service: "invite-member" },
				});
			} finally {
				this.confirmInviteButtonLoading = false;
			}
		},
	},
	async mounted() {
		this.organizationId = await this.userStore.getCurrentUserOrganizationID();
	},
});
</script>
